import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation, inject,
} from '@angular/core';
import { createFormlosFrameDocument, loadFormlosPage } from './formlos-frame';
import { formlosOrigin } from './formlos-origin';

const MIN_FOOTER_HEIGHT = 40;
const MAX_FOOTER_HEIGHT = 4000;

/** The footer uses the same live WordPress document, with its page header hidden. */
@Component({
  encapsulation: ViewEncapsulation.Emulated,
  selector: 'formlos-footer',
  standalone: false,
  styles: `
    :host { display: block; }
    iframe { display: block; width: 100%; border: 0; visibility: hidden; }
    iframe.ready { visibility: visible; }
    .fallback {  background: #e2e2e2; }

    .fallback__inner {
      width: 100%;
      max-width: var(--split-pane-max-width);
      margin: 0 auto;
      display: flex;
      gap: 24px;
      flex-wrap: wrap;
      padding: 36px 40px;
    }
    .fallback a { color: #005064; }
  `,
  template: `
    <iframe #frame title="NAKOS Footer" [class.ready]="loaded" [style.height.px]="height" scrolling="no"></iframe>
    @if (!loaded) {
      <div class="fallback">
        <nav class="fallback__inner" aria-label="Footer">
          <a [href]="origin + '/ueber-nakos/kontakt/'">Kontakt</a>
          <a [href]="origin + '/impressum/'">Impressum</a>
          <a [href]="origin + '/datenschutzerklaerung/'">Datenschutzerklärung</a>
        </nav>
      </div>
    }
  `,
})
export class FormlosFooterComponent implements AfterViewInit, OnDestroy {
  @ViewChild('frame', {
    static: true,
  }) private frame!: ElementRef<HTMLIFrameElement>;

  public readonly origin = formlosOrigin();
  public height = 1;
  public loaded = false;

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly listeners = new AbortController();
  private destroyed = false;

  public ngAfterViewInit(): void {
    window.addEventListener('message', (event) => { this.onMessage(event); }, {
      signal: this.listeners.signal,
    });
    this.load();
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.listeners.abort();
    this.frame.nativeElement.srcdoc = '';
  }

  private onMessage(event: MessageEvent): void {
    if (event.source !== this.frame.nativeElement.contentWindow) { return; }
    const message = event.data;

    if (message?.source !== 'formlos-embed' || message.kind !== 'footer' || message.type !== 'geometry') { return; }
    if (!Number.isFinite(message.height) || message.height < MIN_FOOTER_HEIGHT || message.height > MAX_FOOTER_HEIGHT) { return; }
    this.height = Math.ceil(message.height);
    this.loaded = true;
    this.changeDetector.detectChanges();
  }

  private async load(): Promise<void> {
    try {
      const fragments = await loadFormlosPage(this.origin);

      if (this.destroyed) { return; }
      this.frame.nativeElement.srcdoc = createFormlosFrameDocument(fragments, 'footer', this.origin);
    } catch (error) {
      if (!this.destroyed) { console.error('Could not load Formlos footer frame', error); }
    }
  }
}
